import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Button } from '../components/ui/button';
import { CheckCircle, Target, Heart, Users } from 'lucide-react';
import ConsultationDialog from '../components/ConsultationDialog';

const AboutUs = () => {
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for excellence in every case, delivering superior legal services and results.',
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'We uphold the highest ethical standards and always act in our clients\' best interests.',
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Your needs come first. We build lasting relationships through personalized service.',
    },
  ];

  return (
    <div data-testid="about-us-page">
      <Helmet>
        <title>About Us - Zorvian Associates | Our Story & Mission</title>
        <meta name="description" content="Learn about Zorvian Associates, a professional law firm providing exceptional legal counsel to businesses and individuals nationwide. Discover our mission, values, and commitment to legal excellence." />
        <meta name="keywords" content="about Zorvian Associates, law firm history, legal services mission, attorney values, professional legal counsel, California law firm" />
      </Helmet>
      
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-heading transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              About Zorvian Associates
            </h1>
            <p className={`text-lg text-slate-300 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              A professional law firm providing exceptional legal counsel and representation to businesses and individuals nationwide.
            </p>
          </div>
        </div>
      </section>

      {/* Our Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Company History */}
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6 font-heading">Our Story</h2>
              <div className="max-w-3xl mx-auto text-lg text-slate-600 space-y-4">
                <p>
                  Zorvian Associates was established in San Francisco, California, with a clear mission: 
                  to provide high-quality legal services that are accessible, transparent, and results-driven.
                </p>
                <p>
                  We have grown from a small practice into a full-service law firm 
                  serving clients nationwide. Our team of experienced attorneys has successfully represented 
                  hundreds of clients in matters ranging from complex corporate transactions to specialized 
                  legal services across multiple practice areas.
                </p>
                <p>
                  Today, Zorvian Associates stands as a trusted legal partner for businesses, entrepreneurs, and 
                  individuals who demand excellence, integrity, and personalized attention.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 p-8 rounded-2xl">
                <div className="bg-[#2563EB] w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#0F172A] mb-4 font-heading">Our Mission</h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  To provide exceptional legal services and professional legal counsel that empower businesses and individuals to achieve their goals 
                  with confidence. As a dedicated law firm, we are committed to delivering innovative legal solutions, maintaining the highest ethical 
                  standards, and building lasting relationships based on trust, expertise, and results.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-8 rounded-2xl">
                <div className="bg-[#0F172A] w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#0F172A] mb-4 font-heading">Our Vision</h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  To be the most trusted legal partner for businesses and individuals navigating complex legal challenges. 
                  We envision a future where quality legal counsel is accessible, transparent, and tailored to meet the 
                  unique needs of every client we serve.
                </p>
              </div>
            </div>
            
            <div className="mt-12 bg-white border-2 border-slate-100 p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6 font-heading text-center">What Sets Us Apart</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-6 w-6 text-[#2563EB]" />
                  </div>
                  <h4 className="font-bold text-[#0F172A] mb-2">Client-Centric Approach</h4>
                  <p className="text-sm text-slate-600">Your success is our priority. We listen, understand, and deliver.</p>
                </div>
                <div className="text-center">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-6 w-6 text-[#2563EB]" />
                  </div>
                  <h4 className="font-bold text-[#0F172A] mb-2">Innovative Solutions</h4>
                  <p className="text-sm text-slate-600">We leverage technology and creativity to solve complex legal challenges.</p>
                </div>
                <div className="text-center">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-6 w-6 text-[#2563EB]" />
                  </div>
                  <h4 className="font-bold text-[#0F172A] mb-2">Proven Excellence</h4>
                  <p className="text-sm text-slate-600">Track record of success with 500+ successful cases and 98% satisfaction.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">Our Core Values</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          {/* Mobile: Slider, Desktop: Grid */}
          <div className="block md:hidden max-w-5xl mx-auto">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              autoplay={true}
              autoplaySpeed={3000}
            >
              {values.map((value, index) => (
                <div key={index} className="px-4">
                  <div className="bg-white p-8 rounded-lg shadow-md text-center hover:shadow-xl transition-all duration-300">
                    <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                      <value.icon className="h-8 w-8 text-[#2563EB]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-3 font-heading">{value.title}</h3>
                    <p className="text-slate-600">{value.description}</p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-md text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="h-8 w-8 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3 font-heading">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-6 font-heading">Our Approach</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <CheckCircle className="h-6 w-6 text-[#2563EB] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">Personalized Strategy</h3>
                  <p className="text-slate-600">
                    We recognize that every client and every case is unique. We take the time to understand your specific
                    situation, goals, and concerns before developing a tailored legal strategy.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="h-6 w-6 text-[#2563EB] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">Clear Communication</h3>
                  <p className="text-slate-600">
                    Legal matters can be complex, but our communication doesn't have to be. We explain everything in
                    plain language and keep you informed at every step of the process.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="h-6 w-6 text-[#2563EB] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">Proactive Solutions</h3>
                  <p className="text-slate-600">
                    We don't just react to legal issues—we help you anticipate and prevent them. Our proactive approach
                    saves you time, money, and stress in the long run.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="h-6 w-6 text-[#2563EB] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">Results-Driven</h3>
                  <p className="text-slate-600">
                    Our success is measured by your success. We're committed to achieving the best possible outcomes
                    for our clients through skilled advocacy and strategic thinking.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2563EB] mb-2">500+</div>
              <div className="text-slate-600">Successful Cases</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2563EB] mb-2">15+</div>
              <div className="text-slate-600">Expert Attorneys</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2563EB] mb-2">98%</div>
              <div className="text-slate-600">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6 font-heading">Experience the Zorvian Associates Difference</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-slate-300">
            Let us put our expertise, integrity, and commitment to work for you.
          </p>
          <Button
            onClick={() => setIsConsultationOpen(true)}
            size="lg"
            className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg shadow-xl shadow-blue-500/20"
            data-testid="about-cta-button"
          >
            Schedule Your Free Consultation
          </Button>
        </div>
      </section>

      <ConsultationDialog open={isConsultationOpen} onOpenChange={setIsConsultationOpen} />
    </div>
  );
};

export default AboutUs;
